// Advanced Analytics Tracking System
// High-tech application tracking for NWSISD

import { supabase } from '@/integrations/supabase/client';
import { 
  UserEvent, 
  EventType, 
  EventCategory, 
  DeviceInfo, 
  PerformanceMetrics, 
  UserSession,
  FunnelStep,
  ApplicationPrediction 
} from '@/types/analytics';

class AnalyticsManager {
  private sessionId: string;
  private userId?: string;
  private deviceInfo: DeviceInfo;
  private sessionStartTime: Date;
  private isInitialized: boolean = false;
  private isEnabled: boolean = false;
  private eventQueue: UserEvent[] = [];
  private isOnline: boolean = navigator.onLine;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private lastActivity: Date = new Date();

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = new Date();
    this.deviceInfo = this.collectDeviceInfo();
    this.setupEventListeners();
    this.initializeSession();
    this.setupHeartbeat();
  }

  // Initialize the analytics session
  private async initializeSession(): Promise<void> {
    try {
      // Get current user if authenticated
      const { data: { user } } = await supabase.auth.getUser();
      this.userId = user?.id;

      // Only initialize analytics for admin users to avoid permission errors
      if (!user?.email) {
        return;
      }

      // Check if user has admin role
      const { data: userData } = await supabase
        .from('users_with_roles')
        .select('roles')
        .eq('email', user.email)
        .single();

      if (!userData?.roles?.includes('admin')) {
        return;
      }

      // Enable analytics for admin user
      this.isEnabled = true;

      // Create session record
      await this.createSession();
      
      // Track initial page view
      this.trackEvent('page_view', 'navigation', {
        page: window.location.pathname,
        referrer: document.referrer,
        loadTime: performance.now()
      });

      // Update current page in session
      this.updateCurrentPageInternal(window.location.pathname);

      this.isInitialized = true;
      
      // Process any queued events
      await this.processEventQueue();

      // Send immediate heartbeat to ensure session is active
      this.sendHeartbeat();
      
    } catch (error) {
      console.warn('Analytics initialization failed:', error);
    }
  }

  // Generate unique session ID or reuse existing one
  private generateSessionId(): string {
    // Check if we have an active session in localStorage (within last 60 minutes)
    const existingSession = localStorage.getItem('analytics_current_session');
    if (existingSession) {
      try {
        const sessionData = JSON.parse(existingSession);
        const sessionAge = Date.now() - sessionData.timestamp;
        
        // If session is less than 60 minutes old, reuse it
        if (sessionAge < 60 * 60 * 1000) {
          // Update timestamp to extend session
          localStorage.setItem('analytics_current_session', JSON.stringify({
            sessionId: sessionData.sessionId,
            timestamp: Date.now()
          }));
          
          return sessionData.sessionId;
        }
      } catch (error) {
        console.warn('Failed to parse existing session data:', error);
      }
    }
    
    // Create new session with browser fingerprint to reduce duplicates
    const browserFingerprint = this.getBrowserFingerprint();
    const newSessionId = `session_${Date.now()}_${browserFingerprint}`;
    
    // Store new session data
    localStorage.setItem('analytics_current_session', JSON.stringify({
      sessionId: newSessionId,
      timestamp: Date.now()
    }));
    
    return newSessionId;
  }

  // Create a simple browser fingerprint to help with session uniqueness
  private getBrowserFingerprint(): string {
    // getContext returns null whenever canvas is unavailable — privacy
    // extensions block it, and so do headless environments. This ran at import
    // time behind a non-null assertion, so that null took the whole module down
    // before the app could start. The canvas only adds entropy to a session id,
    // so drop it and keep the rest rather than fail.
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let canvasEntropy = 'no-canvas';
    if (ctx) {
      try {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('Browser fingerprint', 2, 2);
        canvasEntropy = canvas.toDataURL();
      } catch {
        canvasEntropy = 'no-canvas';
      }
    }

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvasEntropy
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(36).substring(0, 10);
  }

  // Collect comprehensive device information
  private collectDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const screen = window.screen;
    
    return {
      type: this.getDeviceType(),
      browser: this.getBrowserName(),
      browserVersion: this.getBrowserVersion(),
      os: this.getOperatingSystem(),
      screenResolution: `${screen.width}x${screen.height}`,
      userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine
    };
  }

  // Device type detection
  private getDeviceType(): 'desktop' | 'tablet' | 'mobile' | 'unknown' {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet';
    if (/mobile|phone|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) return 'mobile';
    return 'desktop';
  }

  // Browser detection
  private getBrowserName(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    return 'Unknown';
  }

  private getBrowserVersion(): string {
    const userAgent = navigator.userAgent;
    const match = userAgent.match(/(chrome|firefox|safari|edge|opera)\/?([\d.]+)/i);
    return match ? match[2] : 'Unknown';
  }

  private getOperatingSystem(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac OS')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  // Collect performance metrics
  private getPerformanceMetrics(): PerformanceMetrics {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const paintEntries = performance.getEntriesByType('paint');
    const navigatorWithConnection = navigator as Navigator & {
      connection?: { effectiveType?: string };
    };
    const performanceWithMemory = performance as Performance & {
      memory?: { usedJSHeapSize?: number };
    };
    
    const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    const lcp = paintEntries.find(entry => entry.name === 'largest-contentful-paint');

    return {
      pageLoadTime: navigation ? navigation.loadEventEnd - navigation.loadEventStart : 0,
      domContentLoadedTime: navigation ? navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart : 0,
      firstContentfulPaint: fcp ? fcp.startTime : 0,
      largestContentfulPaint: lcp ? lcp.startTime : 0,
      firstInputDelay: 0, // Requires additional measurement
      cumulativeLayoutShift: 0, // Requires additional measurement
      connectionType: navigatorWithConnection.connection?.effectiveType || 'unknown',
      memoryUsage: performanceWithMemory.memory?.usedJSHeapSize || 0
    };
  }

  // Create session in database
  private async createSession(): Promise<void> {
    try {
      const sessionData = {
        session_id: this.sessionId,
        user_id: this.userId,
        session_start: this.sessionStartTime.toISOString(),
        device_type: this.deviceInfo.type,
        browser: this.deviceInfo.browser,
        browser_version: this.deviceInfo.browserVersion,
        os: this.deviceInfo.os,
        screen_resolution: this.deviceInfo.screenResolution,
        user_agent: this.deviceInfo.userAgent,
        referrer: document.referrer,
        landing_page: window.location.pathname + window.location.search
      };

      // Use upsert to avoid 409 conflict errors - insert or update if exists
      const { error } = await supabase
        .from('user_sessions')
        .upsert(sessionData, {
          onConflict: 'session_id',
          ignoreDuplicates: false
        });

      if (error) {
        if (error.code === '42501') {
          // Permission denied - silently skip for non-admin users
        } else if (error.code === '42P01') {
          // Table doesn't exist - silently skip
        } else {
          console.warn('Failed to create analytics session:', error.message);
        }

        // Store session data locally as backup
        try {
          localStorage.setItem('analytics_session_backup', JSON.stringify({
            sessionId: this.sessionId,
            userId: this.userId,
            startTime: this.sessionStartTime.toISOString(),
            deviceInfo: this.deviceInfo
          }));
        } catch (storageError) {
          // Silently fail - localStorage might be full or disabled
        }
      }

    } catch (error) {
      // Silently fail - analytics should never break the app
    }
  }

  // Main event tracking method
  public trackEvent(
    eventType: EventType,
    eventCategory: EventCategory,
    properties: Record<string, unknown> = {},
    eventLabel?: string,
    eventValue?: number
  ): void {
    // Skip if analytics is not enabled (non-admin user)
    if (!this.isEnabled) {
      return;
    }
    const event: UserEvent = {
      id: `event_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      userId: this.userId,
      sessionId: this.sessionId,
      eventType,
      eventCategory,
      eventLabel,
      eventValue,
      eventProperties: properties,
      pageUrl: window.location.href,
      pageTitle: document.title,
      timestamp: new Date(),
      deviceInfo: this.deviceInfo,
      performanceMetrics: this.getPerformanceMetrics()
    };

    if (this.isInitialized && this.isOnline) {
      this.sendEvent(event);
    } else {
      this.eventQueue.push(event);
    }
  }

  // Send event to database
  private async sendEvent(event: UserEvent): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_events')
        .insert({
          user_id: event.userId,
          session_id: event.sessionId,
          event_type: event.eventType,
          event_category: event.eventCategory,
          event_label: event.eventLabel,
          event_value: event.eventValue,
          event_properties: event.eventProperties,
          page_url: event.pageUrl,
          page_title: event.pageTitle,
          timestamp: event.timestamp.toISOString(),
          device_info: event.deviceInfo,
          performance_metrics: event.performanceMetrics
        });

      if (error) {
        // Log specific error types for debugging
        if (error.code === '42501') {
          console.warn('Analytics permission denied - user may not have admin role');
        } else if (error.code === '23503') {
          console.warn('Analytics foreign key constraint violation - session may not exist');
        } else if (error.code === '42P01') {
          console.warn('Analytics table does not exist');
        } else {
          console.warn('Analytics event failed:', error.message);
        }
        
        // Store locally as backup instead of throwing
        this.storeEventLocally(event);
      }
      
    } catch (error) {
      console.warn('Analytics event failed:', error);
      // Store in localStorage as backup
      this.storeEventLocally(event);
    }
  }

  // Store event locally as backup when database fails
  private storeEventLocally(event: UserEvent): void {
    try {
      const localEvents = JSON.parse(localStorage.getItem('analytics_backup') || '[]');
      localEvents.push({
        ...event,
        timestamp: event.timestamp.toISOString()
      });
      
      // Keep only last 100 events to prevent storage bloat
      const recentEvents = localEvents.slice(-100);
      localStorage.setItem('analytics_backup', JSON.stringify(recentEvents));
    } catch (storageError) {
      // localStorage might be full or disabled
      console.warn('Could not backup analytics event locally:', storageError);
    }
  }

  // Process queued events
  private async processEventQueue(): Promise<void> {
    if (this.eventQueue.length === 0) return;

    const eventsToProcess = [...this.eventQueue];
    this.eventQueue = [];

    for (const event of eventsToProcess) {
      await this.sendEvent(event);
    }
  }

  // Track application funnel step
  public trackFunnelStep(
    stepName: string,
    stepOrder: number,
    applicationId?: string,
    completed: boolean = false,
    abandonmentReason?: string
  ): void {
    // Public applicants are intentionally not enrolled in the admin analytics
    // pipeline. Treat that as a normal disabled state rather than producing a
    // warning for every form step.
    if (!this.isEnabled || !this.isInitialized) {
      return;
    }

    // Validate application_id - must be null or valid UUID
    const validApplicationId = applicationId && this.isValidUUID(applicationId) ? applicationId : null;

    const funnelData = {
      user_id: this.userId,
      session_id: this.sessionId,
      application_id: validApplicationId,
      step_name: stepName,
      step_order: stepOrder,
      started_at: new Date().toISOString(),
      completed_at: completed ? new Date().toISOString() : null,
      next_step_reached: completed,
      abandonment_reason: abandonmentReason
    };

    // Ensure session exists before inserting funnel data
    this.ensureSessionExists().then(() => {
      supabase
        .from('funnel_analytics')
        .insert(funnelData)
        .then(({ error }) => {
          if (error) console.warn('Failed to track funnel step:', error);
        });
    }).catch((error) => {
      console.warn('Failed to ensure session exists for funnel tracking:', error);
    });
  }

  // Ensure session exists in database
  private async ensureSessionExists(): Promise<void> {
    try {
      // Check if session already exists
      const { data, error } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('session_id', this.sessionId)
        .single();

      if (!data && !error) {
        // Session doesn't exist, create it
        await this.createSession();
      }
    } catch (error) {
      // If session doesn't exist or there's an error, try to create it
      await this.createSession();
    }
  }

  // Helper to validate UUID format
  private isValidUUID(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  // Application-specific tracking methods
  public trackApplicationStarted(applicationId?: string): void {
    this.trackEvent('application_started', 'application', {
      applicationId,
      timestamp: new Date().toISOString()
    });
    
    this.trackFunnelStep('application_started', 1, applicationId, true);
    
    // Only admin analytics sessions exist in user_sessions.
    if (!this.isEnabled || !this.isInitialized) return;

    // Update session to mark application started
    supabase
      .from('user_sessions')
      .update({ application_started: true })
      .eq('session_id', this.sessionId)
      .then(({ error }) => {
        if (error) console.warn('Failed to update session:', error);
      });
  }

  public trackApplicationSubmitted(applicationId: string): void {
    this.trackEvent('application_submitted', 'application', {
      applicationId,
      completionTime: Date.now() - this.sessionStartTime.getTime()
    });
    
    this.trackFunnelStep('application_submitted', 10, applicationId, true);
    
    if (!this.isEnabled || !this.isInitialized) return;

    // Update session to mark application completed
    supabase
      .from('user_sessions')
      .update({ application_completed: true })
      .eq('session_id', this.sessionId)
      .then(({ error }) => {
        if (error) console.warn('Failed to update session:', error);
      });
  }

  public trackFormSectionStarted(sectionName: string, applicationId?: string): void {
    this.trackEvent('form_section_started', 'application', {
      sectionName,
      applicationId
    });
    
    const stepOrder = this.getSectionStepOrder(sectionName);
    this.trackFunnelStep(`section_${sectionName}_started`, stepOrder, applicationId);
  }

  public trackFormSectionCompleted(sectionName: string, applicationId?: string, timeSpent?: number): void {
    this.trackEvent('form_section_completed', 'application', {
      sectionName,
      applicationId,
      timeSpent
    });
    
    const stepOrder = this.getSectionStepOrder(sectionName);
    this.trackFunnelStep(`section_${sectionName}_completed`, stepOrder + 0.5, applicationId, true);
  }

  private getSectionStepOrder(sectionName: string): number {
    const stepMap: Record<string, number> = {
      'student_info': 2,
      'address_validation': 3,
      'school_preferences': 4,
      'transportation': 5,
      'additional_info': 6,
      'review_submit': 7
    };
    return stepMap[sectionName] || 0;
  }

  public trackValidationError(fieldName: string, errorType: string, errorMessage: string): void {
    this.trackEvent('validation_error', 'error', {
      fieldName,
      errorType,
      errorMessage
    });
  }

  public trackAddressValidation(address: string, validationResult: string, timeSpent: number): void {
    this.trackEvent('address_validation_triggered', 'application', {
      address: address.substring(0, 50), // Truncate for privacy
      validationResult,
      timeSpent
    });
  }

  // User experience tracking
  public trackPageExit(): void {
    this.trackEvent('page_exit', 'navigation', {
      timeOnPage: Date.now() - this.sessionStartTime.getTime(),
      scrollDepth: this.getScrollDepth()
    });

    if (!this.isEnabled || !this.isInitialized) return;
    
    // Update session end time
    supabase
      .from('user_sessions')
      .update({ 
        session_end: new Date().toISOString(),
        exit_page: window.location.pathname,
        total_time_seconds: Math.floor((Date.now() - this.sessionStartTime.getTime()) / 1000)
      })
      .eq('session_id', this.sessionId)
      .then(({ error }) => {
        if (error) console.warn('Failed to update session end:', error);
      });
  }

  private getScrollDepth(): number {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    return scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
  }

  // Error tracking
  public trackError(errorType: string, errorMessage: string, stackTrace?: string): void {
    this.trackEvent('javascript_error', 'error', {
      errorType,
      errorMessage,
      stackTrace,
      url: window.location.href,
      userAgent: navigator.userAgent
    });
  }

  // Performance tracking
  public trackSlowPerformance(metric: string, value: number, threshold: number): void {
    this.trackEvent('slow_page_load', 'performance', {
      metric,
      value,
      threshold,
      exceedsThreshold: value > threshold
    });
  }

  // Setup heartbeat system for session management
  private setupHeartbeat(): void {
    // Send heartbeat every 1 minute to keep session active
    this.heartbeatInterval = setInterval(() => {
      if (this.isInitialized && this.isEnabled) {
        this.sendHeartbeat();
      }
    }, 1 * 60 * 1000); // 1 minute

    // Update last activity on any user interaction
    const updateActivity = () => {
      this.lastActivity = new Date();
    };

    // Track user activity
    ['click', 'scroll', 'keypress', 'mousemove', 'touchstart'].forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
  }

  // Send heartbeat to keep session alive
  private async sendHeartbeat(): void {
    // Skip if analytics is not enabled
    if (!this.isEnabled) {
      return;
    }
    
    const timeSinceActivity = Date.now() - this.lastActivity.getTime();
    
    // If no activity for 10 minutes, end session
    if (timeSinceActivity > 10 * 60 * 1000) {
      this.endSession();
      return;
    }

    // Update current page and send heartbeat
    this.updateCurrentPageInternal(window.location.pathname);
    
    // Send heartbeat event
    this.trackEvent('session_heartbeat', 'system', {
      timeSinceActivity: Math.floor(timeSinceActivity / 1000),
      currentPage: window.location.pathname
    });
  }

  // Update current page in session for real-time tracking
  private async updateCurrentPageInternal(page: string): Promise<void> {
    if (!this.isInitialized) return;
    
    try {
      await supabase
        .from('user_sessions')
        .update({
          current_page: page,
          updated_at: new Date().toISOString()
        })
        .eq('session_id', this.sessionId);
    } catch (error) {
      console.warn('Failed to update current page:', error);
    }
  }

  // Setup event listeners for automatic tracking
  private setupEventListeners(): void {
    // NOTE: we intentionally do NOT beacon to '/api/analytics/session-end' — this is a
    // static SPA with no such backend route, so the call only produced a 404 on every
    // page unload. Session end is inferred from the last-seen timestamp written via the
    // normal Supabase analytics path; no synchronous unload request is needed.

    // Track online/offline status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processEventQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Track JavaScript errors
    window.addEventListener('error', (event) => {
      this.trackError('javascript_error', event.message, event.error?.stack);
    });

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError('unhandled_promise_rejection', event.reason?.toString());
    });

    // Track slow performance
    window.addEventListener('load', () => {
      const loadTime = performance.now();
      if (loadTime > 3000) { // 3 second threshold
        this.trackSlowPerformance('page_load', loadTime, 3000);
      }
    });
  }

  // Get session ID for external use
  public getSessionId(): string {
    return this.sessionId;
  }

  // Get device info for external use
  public getDeviceInfo(): DeviceInfo {
    return this.deviceInfo;
  }

  // Public method to update current page
  public updateCurrentPage(page: string): void {
    this.updateCurrentPageInternal(page);
  }

  // Manual session end
  public endSession(): void {
    this.trackPageExit();
    this.isInitialized = false;
    
    // Clean up heartbeat interval
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    // Remove session from localStorage
    localStorage.removeItem('analytics_current_session');
  }
}

// Create global analytics instance
export const analytics = new AnalyticsManager();

// Export analytics functions for use throughout the app
export const trackEvent = analytics.trackEvent.bind(analytics);
export const trackApplicationStarted = analytics.trackApplicationStarted.bind(analytics);
export const trackApplicationSubmitted = analytics.trackApplicationSubmitted.bind(analytics);
export const trackFormSectionStarted = analytics.trackFormSectionStarted.bind(analytics);
export const trackFormSectionCompleted = analytics.trackFormSectionCompleted.bind(analytics);
export const trackValidationError = analytics.trackValidationError.bind(analytics);
export const trackAddressValidation = analytics.trackAddressValidation.bind(analytics);
export const trackError = analytics.trackError.bind(analytics);

// Default export
export default analytics;
